import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { useGoalProgress } from './useGoalProgress'
import { GOAL_FIELDS } from '../health-cycle/goalFields'

const STATE_CLASS = {
  on_track: 'bg-green-100 text-green-800',
  off_track: 'bg-red-100 text-red-800',
  no_data: 'bg-gray-100 text-gray-700',
}

// VH-247 stage 2: an item limit has no title of its own (its title is the code, e.g.
// `sweet_baked_kcal_per_day`); it is named from the UI texts instead.
const actionTitle = (action, t) =>
  action.key?.startsWith('limit:') ? t(`untilNextTest.limits.${action.subject}`, action.title) : action.title

const fmt = (v) => (v === null || v === undefined ? '–' : Math.round(Number(v) * 10) / 10)


// VH-231: target markers and accepted actions between two blood tests.
export function UntilNextTestSection() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useGoalProgress()
  const check = useMutation({
    mutationFn: ({ recommendationId, checked }) => healthCyclesAPI.checkAction(data.cycle.id, recommendationId, checked),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['healthCycleGoalProgress'] }),
  })

  if (isLoading) return <div className="card"><div className="card-body text-sm text-gray-500">{t('common.loading')}</div></div>
  if (isError) {
    return (
      <div className="card" data-testid="until-next-test">
        <div className="card-body text-sm">
          <p className="text-red-600">{t('untilNextTest.error')}</p>
          <button type="button" className="btn-outline mt-2" onClick={() => refetch()}>{t('untilNextTest.retry')}</button>
        </div>
      </div>
    )
  }
  if (!data?.cycle) return null
  const { coverage, target_markers: markers = [], actions = [] } = data
  const low = coverage?.low

  return (
    <div className="card" data-testid="until-next-test">
      <div className="card-header flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">{t('untilNextTest.title')}</h3>
        <Link to={`/health-cycle/cycles/${data.cycle.id}`} className="text-sm text-indigo-600 hover:text-indigo-800">
          {t('untilNextTest.openCycle')}
        </Link>
      </div>
      <div className="card-body space-y-5 text-sm">
        {data.active_goal_differs && data.cycle_goal && (
          <div className="rounded-md bg-yellow-50 p-3" data-testid="goal-differs">
            <p className="text-yellow-800">{t('untilNextTest.goalDiffers')}</p>
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="font-normal">{t('healthCycle.finalize.goal.field')}</th>
                  <th className="font-normal">{t('untilNextTest.activeGoal')}</th>
                  <th className="font-normal">{t('untilNextTest.cycleGoal')}</th>
                </tr>
              </thead>
              <tbody>
                {GOAL_FIELDS.filter((f) => data.cycle_goal[f.key] != null || data.active_goal?.[f.key] != null).map((f) => (
                  <tr key={f.key}>
                    <td className="text-gray-700">{t(`goalFields.${f.key}`)}</td>
                    <td>{fmt(data.active_goal?.[f.key])} {f.unit}</td>
                    <td>{fmt(data.cycle_goal[f.key])} {f.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {markers.length > 0 && (
          <section>
            <h4 className="font-medium text-gray-900">{t('untilNextTest.markers')}</h4>
            <ul className="mt-2 divide-y divide-gray-100">
              {markers.map((m) => (
                <li key={m.marker_code} className="py-2 flex flex-wrap justify-between gap-2">
                  <span className="text-gray-800">{m.marker_name || m.marker_code}</span>
                  <span className="text-gray-600">
                    {t('untilNextTest.baseline')}: {fmt(m.baseline_value)} {m.unit || ''}
                    {m.target != null && <> · {t('untilNextTest.target')}: {fmt(m.target)} {m.unit || ''}</>}
                    {m.direction && <> · {t(`untilNextTest.direction.${m.direction}`, m.direction)}</>}
                  </span>
                  <span className="w-full text-xs text-gray-500">{t('untilNextTest.measuredAtNextTest')}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h4 className="font-medium text-gray-900">{t('untilNextTest.actions')}</h4>
          {coverage && (
            <p className={`mt-1 text-xs ${low ? 'text-yellow-800' : 'text-gray-500'}`} data-testid="coverage">
              {t(low ? 'untilNextTest.coverageLow' : 'untilNextTest.coverage', {
                percent: Math.round((coverage.ratio || 0) * 100), logged: coverage.logged_days, days: coverage.days,
              })}
            </p>
          )}
          {actions.length === 0 ? (
            <p className="mt-2 text-gray-500">{t('untilNextTest.noActions')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100">
              {actions.map((a) => (
                <li key={a.recommendation_id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                  {a.kind === 'checklist' ? (
                    <label className="flex items-center gap-2 text-gray-800">
                      <input
                        type="checkbox"
                        checked={!!a.checked}
                        disabled={check.isPending}
                        onChange={(e) => check.mutate({ recommendationId: a.recommendation_id, checked: e.target.checked })}
                      />
                      {a.title}
                    </label>
                  ) : (
                    <>
                      <span className="text-gray-800">{actionTitle(a, t)}</span>
                      <span className="text-gray-600">
                        {a.progress && a.progress.value != null && (
                          <>{low && `${t('untilNextTest.lowCoverageLabel')} `}{fmt(a.progress.value)} {a.progress.unit || ''}</>
                        )}
                        {a.target && <> · {t('untilNextTest.target')} {a.target.operator} {fmt(a.target.value)} {a.target.unit || ''}</>}
                      </span>
                      {a.state && (
                        <span className={`px-2 py-0.5 rounded text-xs ${STATE_CLASS[a.state] || STATE_CLASS.no_data}`}>
                          {t(`untilNextTest.state.${a.state}`)}
                        </span>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

export default UntilNextTestSection
